(() => {
  'use strict';
  if (customElements.get('haiven-cart-quantity')) return;

  const SOURCE = 'haiven-cart-quantity';
  const text = (key, fallback) => window.haivenCartStrings?.[key] || fallback;
  const positive = (value, fallback) => Number.isSafeInteger(Number(value)) && Number(value) > 0 ? Number(value) : fallback;
  const validId = (value) => /^\d+$/.test(String(value || '')) ? String(value) : '';
  const normalizeRules = (variant) => ({
    id: validId(variant.id),
    available: variant.available === true || variant.available === 'true',
    min: positive(variant.min ?? variant.quantity_rule?.min, 1),
    step: positive(variant.step ?? variant.quantity_rule?.increment, 1),
    max: positive(variant.max ?? variant.quantity_rule?.max, Infinity),
  });
  const nextQuantity = (quantity, direction, rules) => direction > 0
    ? quantity === 0 ? rules.min : quantity + rules.step
    : quantity - rules.step < rules.min ? 0 : quantity - rules.step;
  const isPlain = (item) => !item.selling_plan_allocation && !item.selling_plan &&
    !item.parent_relationship && !item.has_components && !item.item_components?.length &&
    !Object.keys(item.properties || {}).length;
  const isCart = (value) => value && Array.isArray(value.items) && Number.isFinite(value.item_count) &&
    value.items.every((item) => typeof item.key === 'string' && Number.isInteger(item.quantity) && item.quantity >= 0);
  const route = (action) => {
    const configured = window.routes?.[`cart_${action}_url`];
    if (configured && action !== 'get') return configured;
    const root = window.Shopify?.routes?.root || '/';
    return `${root.replace(/\/?$/, '/')}${action === 'get' ? 'cart.js' : `cart/${action}.js`}`;
  };
  const nativeSubscribe = (name, callback) => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS[name]) {
      return subscribe(PUB_SUB_EVENTS[name], callback);
    }
    return () => {};
  };
  const nativePublish = async (name, data) => {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS[name]) {
      try { await publish(PUB_SUB_EVENTS[name], data); } catch (_) {}
    }
  };

  class CartFailure extends Error {
    constructor(message, uncertain = false) {
      super(message);
      this.uncertain = uncertain;
    }
  }

  class SharedCart {
    constructor() {
      this.cart = null;
      this.elements = new Set();
      this.queue = [];
      this.active = null;
      this.running = false;
      this.scheduled = false;
      this.refreshPending = false;
      this.pendingView = null;
      this.errors = new Map();
      this.loadError = '';
      nativeSubscribe('cartUpdate', (event) => {
        if (event?.source === SOURCE) return;
        if (isCart(event?.cartData) && (!this.active || this.active.external)) {
          this.accept(event.cartData);
          this.refreshPending = false;
          if (this.active?.external) this.active.cartReceived = true;
        } else this.refresh();
      });
      ['cart:updated', 'cart:refresh'].forEach((name) => document.addEventListener(name, () => this.refresh()));
      window.addEventListener('pageshow', (event) => { if (event.persisted || !this.cart) this.refresh(); });
      window.addEventListener('focus', () => this.refresh());
      window.addEventListener('online', () => this.refresh());
      document.addEventListener('visibilitychange', () => { if (!document.hidden) this.refresh(); });
    }

    register(element) {
      this.elements.add(element);
      if (!this.cart && !this.running) this.refresh();
      this.notify();
    }

    lines(id) {
      return this.cart?.items.filter((item) => String(item.variant_id ?? item.id) === id && isPlain(item)) || [];
    }

    quantity(id) {
      return this.lines(id).reduce((sum, item) => sum + item.quantity, 0);
    }

    pending(id) {
      return [this.active, ...this.queue].filter((job) => job?.id === id);
    }

    projected(id) {
      return this.pending(id).reduce((quantity, job) => {
        if (job.applied) return quantity;
        if (job === this.active && job.target !== undefined) return job.target;
        return job.directions.reduce((value, direction) => nextQuantity(value, direction, job.rules), quantity);
      }, this.quantity(id));
    }

    notify() {
      this.elements.forEach((element) => element.render());
    }

    accept(cart) {
      if (!isCart(cart)) throw new CartFailure(text('invalid_cart', 'Unable to read the cart. Please try again.'));
      this.cart = cart;
      this.loadError = '';
      document.querySelector('cart-drawer')?.classList.toggle('is-empty', cart.item_count === 0);
      document.querySelector('cart-drawer-items')?.classList.toggle('is-empty', cart.item_count === 0);
      this.notify();
    }

    async request(url, body) {
      const mutation = body !== undefined;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, {
          method: mutation ? 'POST' : 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json', ...(mutation ? { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' } : {}) },
          ...(mutation ? { body: JSON.stringify(body) } : {}),
          signal: controller.signal,
        });
        let data;
        try { data = await response.json(); } catch (_) {
          throw new CartFailure(text('invalid_response', 'The cart returned an unreadable response.'), mutation);
        }
        if (!response.ok || data?.errors || Number(data?.status) >= 400) {
          const detail = data?.description || data?.errors || data?.message;
          throw new CartFailure(typeof detail === 'string' ? detail : text('error', 'The cart could not be updated.'), mutation && response.status >= 500);
        }
        return data;
      } catch (error) {
        if (error instanceof CartFailure) throw error;
        throw new CartFailure(text('connection_error', 'The cart connection was interrupted.'), mutation);
      } finally {
        clearTimeout(timeout);
      }
    }

    async read(beforeAccept) {
      const cart = await this.request(route('get'));
      if (beforeAccept) beforeAccept();
      this.accept(cart);
      return cart;
    }

    refresh() {
      this.refreshPending = true;
      this.schedule();
    }

    schedule() {
      if (this.scheduled || this.running) return;
      this.scheduled = true;
      queueMicrotask(() => {
        this.scheduled = false;
        this.drain();
      });
    }

    enqueue(element, direction) {
      const rules = element.rules();
      if (!this.cart || element.unresolved || !rules || (direction > 0 && !rules.available) || !element.supported()) return;
      const projected = this.projected(rules.id);
      const target = nextQuantity(projected, direction, rules);
      if ((direction < 0 && projected <= 0) || target > rules.max) return;
      this.errors.delete(rules.id);
      const last = this.queue.at(-1);
      if (last?.id === rules.id && !last.external) last.directions.push(direction);
      else this.queue.push({ id: rules.id, rules: { ...rules }, directions: [direction], applied: false });
      this.notify();
      this.schedule();
    }

    runExclusive(callback) {
      return new Promise((resolve, reject) => {
        this.queue.push({ external: callback, resolve, reject });
        this.schedule();
      });
    }

    sectionTargets(job) {
      const targets = [];
      if (document.getElementById('cart-icon-bubble')) targets.push({ section: 'cart-icon-bubble', id: 'cart-icon-bubble', selector: '.shopify-section' });
      ['main-cart-items', 'main-cart-footer'].forEach((id) => {
        const element = document.getElementById(id);
        if (element?.dataset.id) targets.push({ section: element.dataset.id, id, selector: '.js-contents' });
      });
      if (document.getElementById('CartDrawer')) targets.push({ section: 'cart-drawer', id: 'CartDrawer', selector: '#CartDrawer' });
      if (document.getElementById('cart-live-region-text')) targets.push({ section: 'cart-live-region-text', id: 'cart-live-region-text', selector: '.shopify-section' });
      if (document.querySelector('#cart-notification.active')) {
        if (document.getElementById('cart-notification-product')) targets.push({ section: 'cart-notification-product', id: 'cart-notification-product', notification: true, variantId: job?.id });
        if (document.getElementById('cart-notification-button')) targets.push({ section: 'cart-notification-button', id: 'cart-notification-button', selector: '.shopify-section' });
      }
      return targets.slice(0, 5);
    }

    async renderSections(sections, targets) {
      if (!targets.length) return true;
      let content = sections || {};
      if (targets.some((target) => typeof content[target.section] !== 'string')) {
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete('section_id');
          url.searchParams.set('sections', targets.map((target) => target.section).join(','));
          content = await this.request(url.pathname + url.search);
        } catch (_) { return false; }
      }
      let complete = true;
      const drawer = document.querySelector('cart-drawer');
      const focusWasInDrawer = drawer?.contains(document.activeElement);
      const focusedId = focusWasInDrawer ? document.activeElement?.id : null;
      for (const target of targets) {
        const element = document.getElementById(target.id);
        if (!element) continue;
        const html = content[target.section];
        const parsed = typeof html === 'string' && new DOMParser().parseFromString(html, 'text/html');
        let source;
        if (target.notification) {
          const notification = document.querySelector('cart-notification');
          const key = target.variantId ? this.lines(target.variantId).at(-1)?.key : notification?.cartItemKey;
          if (!key || !this.cart?.items.some((item) => item.key === key)) {
            element.textContent = '';
            if (notification?.querySelector('#cart-notification.active')) notification.close?.();
            continue;
          }
          if (notification) notification.cartItemKey = key;
          source = parsed && parsed.getElementById(`cart-notification-product-${key}`);
        } else source = parsed && parsed.querySelector(target.selector);
        if (!source) { complete = false; continue; }
        const destination = target.notification || target.id === 'CartDrawer' || target.selector === '.shopify-section'
          ? element : element.querySelector(target.selector) || element;
        destination.innerHTML = source.innerHTML;
        if (target.id === 'CartDrawer' && drawer) {
          drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close?.());
        }
      }
      if (this.cart) {
        const empty = this.cart.item_count === 0;
        ['cart-drawer', 'cart-drawer-items', 'cart-items', '#main-cart-footer'].forEach((selector) => {
          document.querySelector(selector)?.classList.toggle('is-empty', empty);
        });
      }
      if (drawer?.classList.contains('active') && focusWasInDrawer) {
        const container = drawer.querySelector('.drawer__inner-empty') || drawer.querySelector('.drawer__inner');
        const focusTarget = focusedId && document.getElementById(focusedId) || container?.querySelector('button, a, input');
        if (container && typeof trapFocus === 'function') trapFocus(container, focusTarget || container);
        else focusTarget?.focus();
      }
      return complete;
    }

    announce(job, sectionsRendered = false) {
      const data = { source: SOURCE, cartData: this.cart, variantId: job?.id, productVariantId: job?.id, sectionsRendered };
      nativePublish('cartUpdate', data);
      document.dispatchEvent(new CustomEvent('hf-cart:updated', { detail: data }));
    }

    async flushView() {
      const view = this.pendingView;
      if (!view) return;
      this.pendingView = null;
      const complete = await this.renderSections(view.sections, view.targets);
      if (!complete) this.errors.set(view.job.id, text('section_error', 'Quantity updated, but cart details could not refresh. Please view your cart before checkout.'));
      this.announce(view.job, complete);
      this.notify();
    }

    async mutate(job) {
      if (!this.cart) await this.read();
      const quantity = this.quantity(job.id);
      const target = job.directions.reduce((value, direction) => nextQuantity(value, direction, job.rules), quantity);
      job.target = target;
      if (target > job.rules.max) throw new CartFailure(text('limited', 'The maximum quantity for this option is already in your cart.'));
      if (target === quantity) { job.applied = true; return; }
      const targets = this.sectionTargets(job);
      const context = { sections: targets.map((entry) => entry.section), sections_url: window.location.pathname };
      let result;
      if (target > quantity) {
        result = await this.request(route('add'), { items: [{ id: job.id, quantity: target - quantity }], ...context });
        try { await this.read(() => { job.applied = true; }); } catch (error) {
          error.uncertain = true;
          throw error;
        }
      } else {
        let previous = quantity;
        while (this.quantity(job.id) > target) {
          const line = this.lines(job.id).at(-1);
          const remove = Math.min(line.quantity, this.quantity(job.id) - target);
          result = await this.request(route('change'), { id: line.key, quantity: line.quantity - remove, ...context });
          if (!isCart(result)) throw new CartFailure(text('invalid_response', 'The cart returned an unreadable response.'), true);
          job.applied = this.quantity(job.id) - remove === target;
          this.accept(result);
          if (this.quantity(job.id) >= previous) throw new CartFailure(text('quantity_changed', 'The quantity could not be reduced. Please review your cart.'));
          previous = this.quantity(job.id);
        }
      }
      if (this.quantity(job.id) !== target) {
        throw new CartFailure(text('quantity_changed', 'The cart quantity changed while updating. Please review the confirmed quantity.'));
      }
      job.applied = true;
      this.pendingView = { sections: result?.sections, targets, job };
    }

    async recover(error, job) {
      const canceled = this.queue.filter((queued) => !queued.external && (error.uncertain || queued.id === job.id));
      this.queue = this.queue.filter((queued) => queued.external || (!error.uncertain && queued.id !== job.id));
      this.pendingView = null;
      let refreshed = false;
      try {
        await this.read(() => { job.applied = true; });
        refreshed = true;
        const complete = await this.renderSections(null, this.sectionTargets(job));
        this.announce(job, complete);
      } catch (_) {
        this.cart = null;
        this.loadError = text('refresh_error', 'Unable to confirm your cart. Please view your cart before trying again.');
      }
      const ambiguity = text('uncertain', 'The update may have reached the cart. It was not repeated. Please check the confirmed quantity before trying again.');
      const message = error.uncertain ? ambiguity : error.message;
      this.errors.set(job.id, `${message}${refreshed ? '' : ` ${this.loadError}`}`);
      canceled.forEach((queued) => {
        if (queued.id !== job.id) this.errors.set(queued.id, text('canceled', 'Pending changes were stopped. Please check your cart before trying again.'));
      });
      await nativePublish('cartError', { source: SOURCE, productVariantId: job.id, message, errors: message });
    }

    async drain() {
      if (this.running) return;
      this.running = true;
      try {
        while (this.refreshPending || this.queue.length || this.pendingView) {
          if (this.refreshPending) {
            this.refreshPending = false;
            try { await this.read(); } catch (_) {
              this.cart = null;
              this.loadError = text('load_error', 'Unable to load your cart. Please view your cart or try again when connected.');
              this.queue = this.queue.filter((job) => job.external);
            }
            this.notify();
          }
          if (!this.queue.length) {
            await this.flushView();
            continue;
          }
          const job = this.queue.shift();
          this.active = job;
          this.notify();
          if (job.external) {
            try {
              await this.flushView();
              await this.read();
              const result = await job.external(this.cart);
              if (!job.cartReceived) {
                try { await this.read(); this.refreshPending = false; } catch (_) { this.refreshPending = true; }
              }
              job.resolve(result);
            } catch (error) {
              this.refreshPending = true;
              job.reject(error);
            }
            this.active = null;
            this.notify();
            continue;
          }
          try { await this.mutate(job); } catch (error) { await this.recover(error, job); }
          this.active = null;
          this.notify();
        }
      } finally {
        this.running = false;
        this.notify();
      }
    }
  }

  const client = new SharedCart();
  window.HaivenCartQuantity = { refresh: () => client.refresh(), runExclusive: (callback) => client.runExclusive(callback) };

  class CartQuantity extends HTMLElement {
    connectedCallback() {
      this.connection = (this.connection || 0) + 1;
      const connection = this.connection;
      queueMicrotask(() => { if (this.isConnected && this.connection === connection) this.initialize(); });
    }

    disconnectedCallback() {
      this.connection++;
      client.elements.delete(this);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onSelect);
      this.productInfo?.removeEventListener('change', this.onProductChange);
      this.unsubscribers?.forEach((unsubscribe) => unsubscribe());
      this.setReady(false);
    }

    readVariants(root = this) {
      const script = root.querySelector('script[data-hf-variants]');
      if (!script) return;
      try {
        const data = JSON.parse(script.textContent);
        if (Array.isArray(data)) this.variants = new Map(data.map((variant) => {
          const rules = normalizeRules(variant);
          return [rules.id, rules];
        }));
      } catch (_) {}
    }

    initialize() {
      this.quantityInput = this.querySelector('[data-hf-quantity]');
      this.minus = this.querySelector('[data-hf-minus]');
      this.plus = this.querySelector('[data-hf-plus]');
      this.status = this.querySelector('[data-hf-cart-status]');
      this.error = this.querySelector('[data-hf-cart-error]');
      this.select = this.querySelector('[data-hf-variant-select]');
      if (!this.quantityInput || !this.minus || !this.plus) return;
      this.quantityInput.readOnly = true;
      this.minus.type = this.plus.type = 'button';
      this.status?.setAttribute('aria-live', 'polite');
      this.status?.setAttribute('aria-atomic', 'true');
      this.error?.setAttribute('role', 'alert');
      this.variants = new Map();
      this.readVariants();
      this.productInfo = this.dataset.mode === 'product' ? this.closest('product-info') : null;
      this.unresolved = false;
      this.onClick = (event) => {
        const button = event.target.closest('[data-hf-plus], [data-hf-minus]');
        if (!button || !this.contains(button)) return;
        event.preventDefault();
        event.stopPropagation();
        if (!button.disabled) client.enqueue(this, button.hasAttribute('data-hf-plus') ? 1 : -1);
      };
      this.onSelect = (event) => {
        if (event.target === this.select) {
          this.dataset.variantId = validId(this.select.value);
          this.render();
        }
      };
      this.onProductChange = (event) => {
        if (event.target.matches('input[name="id"]')) {
          this.unresolved = false;
          this.dataset.variantId = validId(this.variantInput()?.value);
        }
        this.render();
      };
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onSelect);
      this.productInfo?.addEventListener('change', this.onProductChange);
      this.unsubscribers = [];
      if (this.productInfo) {
        const input = this.variantInput();
        this.dataset.variantId = validId(input ? input.value : this.dataset.variantId);
        this.unsubscribers.push(nativeSubscribe('optionValueSelectionChange', ({ data } = {}) => {
          if (!this.productInfo.contains(data?.event?.target || data?.target)) return;
          this.unresolved = true;
          this.render();
        }));
        this.unsubscribers.push(nativeSubscribe('variantChange', ({ data } = {}) => {
          const section = this.productInfo.dataset.originalSection || this.productInfo.dataset.section || this.dataset.sectionId;
          if (String(data?.sectionId) !== String(section)) return;
          const updated = Array.from(data.html?.querySelectorAll('haiven-cart-quantity') || []).find((element) =>
            element.dataset.mode === 'product' && element.dataset.productId === this.dataset.productId);
          if (updated) this.readVariants(updated);
          const id = validId(data.variant?.id);
          if (id && !this.variants.has(id)) this.variants.set(id, normalizeRules(data.variant));
          if (id && this.variants.has(id)) this.variants.get(id).available = data.variant.available === true;
          this.dataset.variantId = id;
          this.unresolved = false;
          this.render();
        }));
      } else if (this.select) {
        this.dataset.variantId = validId(this.select.value);
      }
      client.register(this);
    }

    variantInput() {
      if (!this.productInfo) return null;
      const formId = this.dataset.productFormId || `product-form-${this.productInfo.dataset.section || this.dataset.sectionId}`;
      const form = document.getElementById(formId);
      return form && this.productInfo.contains(form) ? form.querySelector('input[name="id"]') : this.productInfo.querySelector('product-form input[name="id"]');
    }

    supported() {
      if (!this.productInfo) return true;
      const form = this.variantInput()?.form || this.variantInput()?.closest('form');
      if (!form) return false;
      return !Array.from(form.querySelectorAll('[name="selling_plan"], [name^="properties["]')).some((field) => !field.disabled && field.value);
    }

    rules() {
      const id = validId(this.dataset.variantId);
      if (!id) return null;
      if (this.variants.has(id)) return this.variants.get(id);
      const option = this.select && Array.from(this.select.options).find((entry) => entry.value === id);
      return option ? normalizeRules({ id, ...option.dataset }) : null;
    }

    setReady(ready) {
      const changed = this.dataset.ready !== String(ready);
      this.dataset.ready = String(ready);
      if (this.productInfo) {
        const anotherReady = Array.from(this.productInfo.querySelectorAll('haiven-cart-quantity')).some((element) => element !== this && element.dataset.ready === 'true');
        if (ready || anotherReady) this.productInfo.dataset.hfCartReady = 'true';
        else delete this.productInfo.dataset.hfCartReady;
      }
      if (ready && changed) this.dispatchEvent(new CustomEvent('hf-cart:ready', { bubbles: true }));
    }

    render() {
      if (!this.quantityInput || !this.isConnected) return;
      const rules = this.rules();
      const id = rules?.id || '';
      const quantity = client.quantity(id);
      const busy = client.pending(id).length > 0;
      const metadataReady = this.variants.size > 0 || (this.select && Array.from(this.select.options).some((option) => option.dataset.available));
      const ready = !!client.cart && !!metadataReady && this.supported();
      const disabled = !ready || this.unresolved || !rules;
      const projected = client.projected(id);
      this.setReady(ready);
      this.quantityInput.value = client.cart ? String(busy ? projected : quantity) : '—';
      this.dataset.confirmedQuantity = String(quantity);
      if (!this.quantityInput.hasAttribute('aria-label')) this.quantityInput.setAttribute('aria-label', text('quantity_label', 'Quantity in cart'));
      this.setAttribute('aria-busy', String(busy || (!client.cart && client.running)));
      this.dataset.busy = String(busy);
      this.minus.disabled = disabled || projected <= 0;
      this.plus.disabled = disabled || !rules.available || nextQuantity(projected, 1, rules) > rules.max;
      this.minus.setAttribute('aria-disabled', String(this.minus.disabled));
      this.plus.setAttribute('aria-disabled', String(this.plus.disabled));
      if (this.error) this.error.textContent = client.errors.get(id) || client.loadError;
      let status;
      if (!client.cart) status = client.loadError ? '' : text('loading', 'Loading cart…');
      else if (!this.supported()) status = text('specialized', 'Use the product options to add this item.');
      else if (this.unresolved) status = text('resolving', 'Updating options…');
      else if (!rules) status = text('choose_variant', 'Choose an option to change its cart quantity.');
      else if (busy) status = text('updating', 'Updating cart…');
      else if (!rules.available) status = text('unavailable', 'This option is unavailable.');
      else status = text('quantity_in_cart', '[quantity] in cart').replace('[quantity]', quantity);
      if (this.status) this.status.textContent = status;
    }
  }

  customElements.define('haiven-cart-quantity', CartQuantity);
})();
